import React from 'react';
import queryString from 'query-string';

import Loading from '$/components/Loading';
import { PlayerMode } from '$/components/WebinarPlayer/types';
import { useEmbeddedPlayerContext } from '$/components/EmbeddedPlayer/useEmbeddedPlayer';

import { logger } from '$/utils/logger';
import { Redirect } from '$/utils/Redirect';
import { LocalTimeZone } from '$/utils/LocalTimeZone';
import { getVisitorId, setVisitorId } from '$/utils/visitor';
import { useServerContext } from '$/hooks/useServerDateNow';

import {
  AttendeeUrls,
  SessionType,
  SubscribedStatus,
  UngatedSessionType,
  useAttendeeJoinUngatedSessionMutation,
  useAttendeeJoinWebinarLiteMutation,
  useAttendeeJoinWebinarMutation,
  useGetAttendeeQuery,
  usePublicSetQuery,
} from '$/GraphQL/schema/schema';

import { ViewProps } from './types';
import {
  AttendeeContext,
  AttendeeContextState,
  AttendeeJoinParams,
  AttendeeJoinResult,
} from './AttendeeContext';
import useRunOnce from '$/hooks/useRunOnce';
import { WidgetType } from '$/widget/src/interface';

export const AttendeeContextProvider: React.FC<ViewProps & { children: React.ReactNode }> = ({
  mode,
  setId,
  isTest,
  children,
  attendeeId: urlAttendeeId,
}) => {
  const query = queryString.parse(location.search) as {
    source?: string;
  } & { [key: string]: string }; // For any propertyName values
  // We are sometimes seeing attendeeId in the URL of this form due to poor email clients
  // "2119366<https://click-ca.ewebinar.com/CL0/https:%2F%2Flovealgos.ewebinar.com%2Fwebinar%2F4404%2Fjoin%2F2119366/1/010d0180d4c05b2f-4ee73f5f-4596-4c9b-ac87-7eac7f6f994f-000000/-g3qSxUr5ix7V51S5kT_ktxFKf6ZZt40RT4gYbJHn5g=55>"
  // so grab the digits at the beginning only
  const [attendeeId, setAttendeeId] = React.useState(() => {
    const matches = /(\d)+/.exec(urlAttendeeId ?? '');
    return matches?.[0] ?? urlAttendeeId;
  });
  const [loading, setLoading] = React.useState(true);

  const isReplay = mode === PlayerMode.replay;
  const ungatedSessionType = isReplay ? UngatedSessionType.Replay : UngatedSessionType.OnDemand;

  const { setServerDate } = useServerContext();
  const { playerInfo, isEmbeddedPlayer } = useEmbeddedPlayerContext();

  const [joinWebinarLite] = useAttendeeJoinWebinarLiteMutation();
  const [joinWebinar, joinResults] = useAttendeeJoinWebinarMutation();
  const [joinUngatedSession, joinUngatedSessionResult] = useAttendeeJoinUngatedSessionMutation();
  const publicSetQuery = usePublicSetQuery({
    variables: {
      id: setId!,
    },
    skip: !isEmbeddedPlayer,
  });

  const anonymousAttendee = React.useMemo(() => {
    if (!publicSetQuery.data?.publicSet?.publicWebinar) return undefined;
    const attendee: AttendeeContextState['attendee'] = {
      id: 'anonymous',
      optOut: false,
      subscribed: SubscribedStatus.Subscribed,
      firstName: 'Attendee',
      visitorId: 'anonymous',
      isOnDemand: ungatedSessionType === UngatedSessionType.OnDemand,
      sessionType:
        ungatedSessionType === UngatedSessionType.OnDemand
          ? SessionType.JustInTime
          : SessionType.Replay,
      startTime: new Date().toISOString(),
      urls: {} as AttendeeUrls,
      ewebinar: {
        ...publicSetQuery.data.publicSet.publicWebinar,
        interactions: [],
      },
      set: publicSetQuery.data.publicSet,
    };
    return attendee;
  }, [publicSetQuery.data?.publicSet, ungatedSessionType]);

  const joinedAttendee =
    joinResults.data?.attendeeJoinWebinar ??
    joinUngatedSessionResult.data?.attendeeJoinUngatedSession;

  // The Attendee returned from the Join mutation won't update as the Attendee cache updates
  // so we call this query which should go 100% off the cache after Join is done so that our attendee
  // object updates all the way down via WebinarPlayerContext
  const attendeeCacheQuery = useGetAttendeeQuery({
    variables: { id: String(attendeeId), setId: String(setId) },
    fetchPolicy: isTest ? 'cache-first' : 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    skip: !joinedAttendee,
  });
  const attendee = attendeeCacheQuery.data?.attendee;

  const onJoinedAttendeeUpdate = React.useCallback(
    (attendee?: { id?: string; visitorId?: string; now?: string } | null): void => {
      if (attendee?.visitorId) {
        setVisitorId(attendee?.visitorId);
      }
      if (attendee?.id) {
        setAttendeeId(attendee?.id);
      }
      if (attendee?.now) {
        setServerDate(new Date(attendee?.now));
      }
    },
    [setServerDate]
  );

  const attendeeJoinWebinar = React.useCallback(
    async ({ joinTime, connectionId }: AttendeeJoinParams = {}): Promise<AttendeeJoinResult> => {
      if (!setId || !attendeeId) return;
      const attendeeJoinResult = await joinWebinar({
        variables: {
          id: attendeeId,
          setId,
          joinTime,
          connectionId,
          joiningReplay: isReplay,
          visitorId: getVisitorId(),
        },
        update: (_cache, mutationResult) => {
          onJoinedAttendeeUpdate(mutationResult.data?.attendeeJoinWebinar);
          setLoading(false);
        },
      });
      return attendeeJoinResult.data?.attendeeJoinWebinar;
    },
    [attendeeId, isReplay, joinWebinar, onJoinedAttendeeUpdate, setId]
  );

  const attendeeJoinWebinarLite = React.useCallback(
    async ({ joinTime, connectionId }: AttendeeJoinParams = {}): Promise<AttendeeJoinResult> => {
      if (isEmbeddedPlayer) return; // Skip it because we already saved join time in the join ungated query for embedded player
      if (!joinedAttendee) return; // Should have joinedAttendee here because we only call it to set joinTime after play join success
      const attendeeJoinResult = await joinWebinarLite({
        variables: {
          id: joinedAttendee.id,
          setId: joinedAttendee.set.id,
          visitorId: getVisitorId(),
          joinTime,
          connectionId,
        },
        update: async (_cache, mutationResult) => {
          onJoinedAttendeeUpdate(mutationResult.data?.attendeeJoinWebinar);
        },
      });
      return attendeeJoinResult.data?.attendeeJoinWebinar;
    },
    [joinWebinarLite, joinedAttendee, onJoinedAttendeeUpdate, isEmbeddedPlayer]
  );

  const attendeeJoinUngatedSession = React.useCallback(
    async ({ joinTime, connectionId }: AttendeeJoinParams = {}): Promise<AttendeeJoinResult> => {
      if (!setId) return;
      const timezone = LocalTimeZone.name;
      try {
        const attendeeJoinResult = await joinUngatedSession({
          variables: {
            data: {
              setId,
              timezone,
              origin: playerInfo?.href ?? document.location.href,
              referrer: playerInfo?.referrer ?? document.referrer,
              visitorId: getVisitorId(),
              sessionType: ungatedSessionType,
              source: query?.source ?? undefined,
              ...(isEmbeddedPlayer ? { widgetType: WidgetType.EmbeddedPlayer } : {}),
            },
            joinTime,
            connectionId,
          },
          update: async (_cache, mutationResult) => {
            if (mutationResult.data) {
              onJoinedAttendeeUpdate(mutationResult.data?.attendeeJoinUngatedSession);
            }
          },
        });
        return attendeeJoinResult.data?.attendeeJoinUngatedSession;
      } catch (e) {
        const error = e as Error;
        logger.error(`joinUngatedReplayQuery ERROR ${error.message}`, { error });
        return undefined;
      } finally {
        setLoading(false);
      }
    },
    [
      setId,
      joinUngatedSession,
      playerInfo?.href,
      playerInfo?.referrer,
      ungatedSessionType,
      query?.source,
      onJoinedAttendeeUpdate,
      isEmbeddedPlayer,
    ]
  );

  const join = React.useCallback(
    async (params?: AttendeeJoinParams) => {
      if (joinedAttendee) return attendeeJoinWebinarLite(params);
      if (!urlAttendeeId) return attendeeJoinUngatedSession(params);
      return attendeeJoinWebinar(params);
    },
    [
      urlAttendeeId,
      joinedAttendee,
      attendeeJoinUngatedSession,
      attendeeJoinWebinar,
      attendeeJoinWebinarLite,
    ]
  );

  useRunOnce(() => {
    // Embedded player will use anonymous attendee first
    // then create an real attendee after clicks on the join button
    if (isEmbeddedPlayer) return;
    join();
  });

  React.useEffect(() => {
    if (!anonymousAttendee) return;
    setLoading(false);
  }, [anonymousAttendee]);

  if (loading) return <Loading />;

  if (!isEmbeddedPlayer && !attendeeId) return <Redirect to={`/webinar/${setId}/register`} />;

  if (!isEmbeddedPlayer && !joinedAttendee) {
    console.warn('JOIN RESULTS ', urlAttendeeId ? joinResults : joinUngatedSessionResult);
    return <Loading query={joinUngatedSessionResult} />;
  }

  if (joinedAttendee && !joinedAttendee.ewebinar) {
    console.error(`Attendee webinar ${attendee?.ewebinar} not found`);
    return <Redirect to={`/webinar/${setId}`} />;
  }

  if (!isEmbeddedPlayer && !attendee) {
    return <Loading query={attendeeCacheQuery} />;
  }

  const attendeeOrLocalAttendee = attendee ?? anonymousAttendee;
  const ewebinar = joinedAttendee?.ewebinar ?? anonymousAttendee!.ewebinar;
  const set = joinedAttendee?.set ?? anonymousAttendee!.set;

  if (!attendeeOrLocalAttendee) {
    return <Redirect to={`/webinar/${setId}/register`} />;
  }

  const value = {
    loading,
    attendee: {
      ...attendeeOrLocalAttendee,
      // update properties from attendeeJoinWebinar response
      properties: joinedAttendee?.properties ?? [],
      ewebinar,
      set,
    },
    join,
  };

  return <AttendeeContext.Provider value={value}>{children}</AttendeeContext.Provider>;
};
